/**
 * 仪表盘首页。
 *
 * 展示账户余额 / 本月用量 / API Key 数 / 本月消耗四项指标；
 * 用量趋势折线图（useUsageOverview + UsageLineChart）；
 * 提供常用功能快捷入口。
 *
 * @module pages/DashboardPage
 */
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InsightsIcon from '@mui/icons-material/Insights';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LoyaltyIcon from '@mui/icons-material/Loyalty';

import PageHeader from '@/components/common/PageHeader';
import StatCard from '@/components/common/StatCard';
import MoneyText from '@/components/common/MoneyText';
import UsageLineChart from '@/components/charts/UsageLineChart';
import Loading from '@/components/common/Loading';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/authStore';
import { useUsageOverview } from '@/hooks/useUsageOverview';
import { useApiKeys } from '@/hooks/useApiKeys';
import { formatNumber } from '@/utils/format';

/** 快捷入口定义。 */
interface QuickLink {
  label: string;
  path: string;
  icon: JSX.Element;
}

/** 快捷入口列表。 */
const QUICK_LINKS: QuickLink[] = [
  { label: '管理 API Keys', path: '/apikeys', icon: <VpnKeyIcon /> },
  { label: '查看用量', path: '/usage', icon: <InsightsIcon /> },
  { label: '去充值', path: '/recharge', icon: <AccountBalanceWalletIcon /> },
  { label: '浏览套餐', path: '/packages', icon: <LoyaltyIcon /> },
];

/**
 * 仪表盘首页组件。
 *
 * @returns 页面元素。
 */
export default function DashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const balance = useAuthStore((s) => s.user?.balance ?? '0');
  const { data: overview, loading } = useUsageOverview();
  const { data: keys } = useApiKeys();
  const { Toast } = useToast();

  const totalCost = overview?.totalCost ?? '0';
  const totalTokens = overview?.totalTokens ?? 0;
  const requestCount = overview?.requestCount ?? 0;
  const keyCount = keys?.length ?? 0;
  const trend = overview?.dailyTrend ?? [];

  return (
    <Box>
      <PageHeader title="仪表盘" subtitle="欢迎使用寇豆码，这里是你账户的全局概览" />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4,1fr)' },
          gap: 3,
          mb: 3,
        }}
      >
        <StatCard
          title="账户余额"
          value={<MoneyText value={balance} />}
          icon={<AccountBalanceWalletIcon />}
          color="#4f46e5"
        />
        <StatCard
          title="本月用量 (Tokens)"
          value={formatNumber(totalTokens)}
          icon={<InsightsIcon />}
          color="#0ea5e9"
        />
        <StatCard
          title="API Key 数"
          value={keyCount}
          icon={<VpnKeyIcon />}
          color="#16a34a"
        />
        <StatCard
          title="本月消耗"
          value={<MoneyText value={totalCost} />}
          icon={<ReceiptLongIcon />}
          color="#f59e0b"
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          gap: 3,
        }}
      >
        <Box>
          {loading && trend.length === 0 ? (
            <Loading />
          ) : (
            <UsageLineChart data={trend} metric="cost" />
          )}
        </Box>

        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              快捷入口
            </Typography>
            <Typography variant="caption" color="text.secondary">
              本月共 {formatNumber(requestCount)} 次请求
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, mt: 2 }}>
              {QUICK_LINKS.map((link) => (
                <Button
                  key={link.path}
                  variant="outlined"
                  startIcon={link.icon}
                  onClick={() => navigate(link.path)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
      {Toast}
    </Box>
  );
}
